import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Edit, Trash2, Key, Mail, User, Shield, X, Check, UserCog, Eye, Plus } from 'lucide-react';
import { useAlert } from '../context/AlertContext';
import { useAuth } from '../context/AuthContext';
import { formatRoleName, isAdmin as isAdminUser } from '../utils/roleUtils';
import { formatErrorMessage } from '../utils/errorFormatter';

// BUG-019/020: Site roles feature is temporarily disabled in the UI.
// Backend /user-site-roles endpoints return 503; UI hides related actions.
const SITE_ROLES_ENABLED = false;

const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UserManagementTable = ({ users, departments = [], onUpdate }) => {
  const { showSuccess, showError, showInfo, confirm } = useAlert();
  const { user: currentUser } = useAuth();
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSiteSearch, setEditSiteSearch] = useState('');
  const [showPasswordReset, setShowPasswordReset] = useState(null);
  const [resetLink, setResetLink] = useState('');

  // Role management state
  const [showRoleModal, setShowRoleModal] = useState(null);
  const [showPermissionsModal, setShowPermissionsModal] = useState(null);
  const [roles, setRoles] = useState([]);
  const [userRoles, setUserRoles] = useState([]);
  const [userPermissions, setUserPermissions] = useState(null);
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [roleLoading, setRoleLoading] = useState(false);

  // Site role management state
  const [showSiteRolesModal, setShowSiteRolesModal] = useState(null);
  const [userSiteRoles, setUserSiteRoles] = useState([]);
  const [sites, setSites] = useState([]);
  const [siteRoleForm, setSiteRoleForm] = useState({ site_node_id: '', role_id: '', expires_at: '' });
  const [siteRoleLoading, setSiteRoleLoading] = useState(false);
  const [siteRoleError, setSiteRoleError] = useState(null);
  const [revokeConfirmId, setRevokeConfirmId] = useState(null);

  // Load available roles and sites on mount
  useEffect(() => {
    loadRoles();
    loadSites();
  }, []);

  const loadRoles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/tenant-admin/roles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRoles(response.data || []);
    } catch (err) {
      console.error('Failed to load roles:', err);
    }
  };

  const loadUserRoles = async (userId) => {
    setRoleLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/tenant-admin/users/${userId}/roles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserRoles(response.data?.roles || []);
    } catch (err) {
      console.error('Failed to load user roles:', err);
      setUserRoles([]);
    } finally {
      setRoleLoading(false);
    }
  };

  const loadUserPermissions = async (userId) => {
    setRoleLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/tenant-admin/users/${userId}/roles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserPermissions(response.data);
    } catch (err) {
      console.error('Failed to load user permissions:', err);
      setUserPermissions(null);
    } finally {
      setRoleLoading(false);
    }
  };

  const handleManageRoles = async (user) => {
    setShowRoleModal(user);
    await loadUserRoles(user.id);
  };

  const loadSites = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/org/sites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data;
      setSites(Array.isArray(data) ? data : data.sites ?? []);
    } catch (err) {
      console.error('Failed to load sites:', err);
    }
  };

  const loadUserSiteRoles = async (userId) => {
    setSiteRoleLoading(true);
    setSiteRoleError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/rbac/user-site-roles/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserSiteRoles(response.data || []);
    } catch (err) {
      console.error('Failed to load user site roles:', err);
      setUserSiteRoles([]);
    } finally {
      setSiteRoleLoading(false);
    }
  };

  const handleManageSiteRoles = async (user) => {
    setShowSiteRolesModal(user);
    setSiteRoleForm({ site_node_id: '', role_id: '', expires_at: '' });
    setSiteRoleError(null);
    setRevokeConfirmId(null);
    await loadUserSiteRoles(user.id);
  };

  const handleAssignSiteRole = async (userId) => {
    if (!siteRoleForm.role_id) {
      setSiteRoleError('Role is required');
      return;
    }
    setSiteRoleLoading(true);
    setSiteRoleError(null);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/rbac/user-site-roles`, {
        user_id: userId,
        site_node_id: siteRoleForm.site_node_id || null,
        role_id: siteRoleForm.role_id,
        expires_at: siteRoleForm.expires_at || null,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showSuccess('Site role assigned successfully');
      setSiteRoleForm({ site_node_id: '', role_id: '', expires_at: '' });
      await loadUserSiteRoles(userId);
    } catch (err) {
      setSiteRoleError(err.response?.data?.detail || err.message);
    } finally {
      setSiteRoleLoading(false);
    }
  };

  const handleRevokeSiteRole = async (userId, assignmentId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/rbac/user-site-roles/${assignmentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showSuccess('Site role revoked');
      setRevokeConfirmId(null);
      await loadUserSiteRoles(userId);
    } catch (err) {
      showError(formatErrorMessage(err, 'Failed to revoke site role'));
    }
  };

  const handleViewPermissions = async (user) => {
    setShowPermissionsModal(user);
    await loadUserPermissions(user.id);
  };

  const handleAssignRole = async (userId) => {
    if (!selectedRoleId) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/tenant-admin/users/${userId}/roles`,
        { role_id: selectedRoleId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccess('Role assigned successfully');
      setSelectedRoleId('');
      await loadUserRoles(userId);
      onUpdate();
    } catch (err) {
      showError('Failed to assign role: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleRevokeRole = async (userId, roleId) => {
    const confirmed = await confirm(
      'Revoke Role',
      'Are you sure you want to revoke this role from the user?',
      { destructive: true, confirmLabel: 'Revoke' }
    );
    if (!confirmed) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/tenant-admin/users/${userId}/roles/${roleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showSuccess('Role revoked successfully');
      await loadUserRoles(userId);
      onUpdate();
    } catch (err) {
      // The API returns a structured 409 (message + the open items that block
      // the revoke); formatErrorMessage renders both as a readable sentence.
      showError(formatErrorMessage(err, 'Failed to revoke role'));
    }
  };

  // Site assignment only applies to site-scoped roles; corporate roles always
  // span all sites. Returns 'site' | 'corporate' | 'none'.
  const getSiteScope = (user) => {
    const userRoles = user.rbac_roles || [];
    if (userRoles.length === 0) return 'none';
    const scopes = userRoles.map(r => roles.find(x => x.id === r.id)?.scope_type || 'site');
    return scopes.some(s => s !== 'corporate') ? 'site' : 'corporate';
  };

  const handleEditClick = (user) => {
    const currentSiteIds = (user.sites || []).map(s => s.id);
    setEditingUser(user.id);
    setEditSiteSearch('');
    setEditForm({
      name: user.name,
      email: user.email,
      department_id: user.department_id || '',
      site_node_ids: currentSiteIds,
      _original_site_ids: currentSiteIds,
      _site_scope: getSiteScope(user),
    });
  };

  const handleCancelEdit = () => {
    setEditingUser(null);
    setEditForm({});
  };

  const toggleEditSite = (siteId) => {
    const selected = editForm.site_node_ids || [];
    setEditForm({
      ...editForm,
      site_node_ids: selected.includes(siteId)
        ? selected.filter(id => id !== siteId)
        : [...selected, siteId],
    });
  };

  const handleSaveEdit = async (userId) => {
    // Department is mandatory — the backend rejects a blank value, so catch it
    // here with a readable message instead of surfacing a 422.
    if (!editForm.department_id) {
      showError('Department is required. It determines which Safety Manager receives incidents for this user\'s area.');
      return;
    }

    const { _original_site_ids: originalSiteIds = [], _site_scope: siteScope, site_node_ids: siteIds = [], ...payload } = editForm;
    if (siteScope === 'site') {
      if (siteIds.length === 0) {
        showError('Select at least one site for this user.');
        return;
      }
      const sitesChanged = siteIds.length !== originalSiteIds.length
        || siteIds.some(id => !originalSiteIds.includes(id));
      // Only send sites when they changed, so a plain name/department edit
      // never rewrites the user's site assignments.
      if (sitesChanged) payload.site_node_ids = siteIds;
    }

    // BUG-018: Confirmation before saving user edits
    const confirmed = await confirm(
      'Save Changes?',
      'Are you sure you want to save these changes to the user?',
      { confirmLabel: 'Save' }
    );
    if (!confirmed) return;

    try {
      const token = localStorage.getItem('token');
      // Use PostgreSQL tenant-admin endpoint
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/tenant-admin/users/${userId}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setEditingUser(null);
      setEditForm({});
      showSuccess('User updated successfully');

      // BUG-012: If the backend signals that the session needs to re-auth
      // (e.g., the current user changed their own email), force logout.
      if (response?.data?.requires_relogin) {
        showInfo('Your email was changed. Please log in again.');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setTimeout(() => { window.location.href = '/login'; }, 1500);
        return;
      }

      onUpdate(); // Refresh the user list
    } catch (error) {
      showError('Error updating user: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDelete = async (userId, userName) => {
    const confirmed = await confirm(
      'Delete User',
      `Are you sure you want to delete ${userName}? This action cannot be undone.`,
      { destructive: true, confirmLabel: 'Delete' }
    );

    if (!confirmed) return;

    try {
      const token = localStorage.getItem('token');
      // Use PostgreSQL tenant-admin endpoint
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/tenant-admin/users/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      showSuccess(`User ${userName} deleted successfully`);
      onUpdate(); // Refresh the user list
    } catch (error) {
      showError('Error deleting user: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handlePasswordReset = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/${userId}/send-password-reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Show reset link only if email wasn't sent (SMTP not configured)
      if (!response.data.email_sent && response.data.reset_link) {
        setResetLink(response.data.reset_link);
        setShowPasswordReset(userId);
      } else {
        showSuccess(`Password reset email sent successfully to ${response.data.user_email}! The user will receive a secure reset link valid for 30 minutes.`);
      }
    } catch (error) {
      showError('Error generating reset link: ' + (error.response?.data?.detail || error.message));
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showSuccess('Reset link copied to clipboard!');
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                {/* BUG-269: System vs Site Role clarity. Site Role column is
                    flag-guarded until SITE_ROLES_ENABLED is re-enabled. */}
                System Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Department
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Site(s)
              </th>
              <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.map((user) => (
              <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                {editingUser === user.id ? (
                  // Edit Mode
                  <>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="text"
                        value={editForm.name}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        placeholder="Full Name"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {/* BUG-005/011: Admins cannot edit their own email. */}
                      {(() => {
                        const isEditingSelf = currentUser && user.id === currentUser.id;
                        const isAdminRole = currentUser && isAdminUser(currentUser);
                        const lockEmail = isEditingSelf && isAdminRole;
                        return (
                          <input
                            type="email"
                            value={editForm.email}
                            onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                            disabled={lockEmail}
                            title={lockEmail ? 'Admins cannot change their own email address' : undefined}
                            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${lockEmail ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            placeholder="Email"
                          />
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-gray-400 text-sm italic">Use "Manage Roles" to change</span>
                    </td>
                    {/* Department is editable here because it drives incident
                        assignment routing, and users created before it became
                        mandatory would otherwise have no way to get one. */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select
                        value={editForm.department_id || ''}
                        onChange={(e) => setEditForm({ ...editForm, department_id: e.target.value })}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      >
                        <option value="">— Select department —</option>
                        {departments.map((d) => (
                          <option key={d.id} value={d.id}>{d.name}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-6 py-4 align-top">
                      {editForm._site_scope === 'site' ? (() => {
                        const selected = editForm.site_node_ids || [];
                        const query = editSiteSearch.trim().toLowerCase();
                        const visibleSites = query
                          ? sites.filter(s => (s.name || '').toLowerCase().includes(query))
                          : sites;
                        const allSelected = sites.length > 0 && selected.length === sites.length;
                        return (
                          <div className="w-56">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-medium text-gray-700">
                                {selected.length} of {sites.length} selected
                              </span>
                              <button
                                type="button"
                                onClick={() => setEditForm({
                                  ...editForm,
                                  site_node_ids: allSelected ? [] : sites.map(s => s.id),
                                })}
                                className="text-xs text-blue-600 hover:underline"
                              >
                                {allSelected ? 'Clear all' : 'All sites'}
                              </button>
                            </div>
                            <input
                              type="text"
                              value={editSiteSearch}
                              onChange={(e) => setEditSiteSearch(e.target.value)}
                              placeholder="Search sites…"
                              className="w-full px-2 py-1 mb-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                            <div className="max-h-40 overflow-y-auto border border-gray-200 rounded">
                              {visibleSites.length === 0 ? (
                                <div className="px-2 py-1 text-xs text-gray-400">No matching sites</div>
                              ) : visibleSites.map(s => (
                                <label key={s.id} className="flex items-center gap-2 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={selected.includes(s.id)}
                                    onChange={() => toggleEditSite(s.id)}
                                    className="rounded border-gray-300"
                                  />
                                  <span className="truncate" title={s.name}>{s.name}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        );
                      })() : (
                        <span className="text-gray-400 text-sm italic">
                          {editForm._site_scope === 'corporate'
                            ? 'All sites (corporate role)'
                            : 'Assign a role before assigning sites'}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleSaveEdit(user.id)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          title="Save"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                          title="Cancel"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </>
                ) : (
                  // View Mode
                  <>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <User className="h-5 w-5 text-blue-600" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-semibold text-gray-900">{user.name}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-900">
                        <Mail className="w-4 h-4 text-gray-400 mr-2" />
                        {user.email}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {/* Roles from user_roles table */}
                        {user.rbac_roles && user.rbac_roles.length > 0 ? (
                          user.rbac_roles.map((rbacRole) => (
                            <span
                              key={rbacRole.id}
                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800"
                              title={rbacRole.display_name || formatRoleName(rbacRole.name)}
                            >
                              <Shield className="w-3 h-3 mr-1" />
                              {/* BUG-017: Render role in human-readable Title Case */}
                              {rbacRole.display_name || formatRoleName(rbacRole.name)}
                            </span>
                          ))
                        ) : (
                          <span className="text-gray-400 text-sm">No roles assigned</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-700">{user.department || '—'}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {user.sites && user.sites.length > 0 ? (
                          user.sites.map((site) => (
                            <span
                              key={site.id}
                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700"
                              title={site.name}
                            >
                              {site.name}
                            </span>
                          ))
                        ) : (
                          <span className="text-gray-400 text-sm">All sites</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleEditClick(user)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit User"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleManageRoles(user)}
                          className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                          title="Manage Roles"
                        >
                          <UserCog className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleViewPermissions(user)}
                          className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                          title="View Permissions"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {/* BUG-019/020: Site roles temporarily disabled */}
                        {/* Manage Site Roles button removed until feature is re-enabled */}
                        <button
                          onClick={() => handlePasswordReset(user.id)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          title="Send Password Reset"
                        >
                          <Key className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(user.id, user.name)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete User"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Password Reset Modal */}
      {showPasswordReset && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Password Reset Link Generated</h3>
              <button
                onClick={() => setShowPasswordReset(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4">
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded mb-3">
                <p className="text-sm text-yellow-800">
                  <strong>⚠️ SMTP Not Configured:</strong> Email was not sent. Share this link with the user manually.
                </p>
              </div>
              <p className="text-sm text-gray-600 mb-3">
                This reset link will expire in <strong>30 minutes</strong>.
              </p>
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg break-all text-sm font-mono">
                {resetLink}
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => copyToClipboard(resetLink)}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Copy Link
              </button>
              <button
                onClick={() => setShowPasswordReset(null)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manage Roles Modal */}
      {showRoleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Manage Roles - {showRoleModal.name}</h3>
                <p className="text-sm text-gray-600">{showRoleModal.email}</p>
              </div>
              <button
                onClick={() => {
                  setShowRoleModal(null);
                  setUserRoles([]);
                  setSelectedRoleId('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {roleLoading ? (
              <div className="text-center py-8 text-gray-500">Loading roles...</div>
            ) : (
              <div>
                {/* Current Roles */}
                <div className="mb-6">
                  <h4 className="text-md font-semibold mb-3 text-gray-800">Current Roles</h4>
                  {userRoles.length > 0 ? (
                    <div className="space-y-2">
                      {userRoles.map((role) => (
                        <div
                          key={role.id}
                          className="flex items-center justify-between p-3 border rounded-lg bg-gray-50"
                        >
                          <div>
                            {/* BUG-272 */}
                            <div className="font-medium text-gray-900">{role.display_name || formatRoleName(role.name)}</div>
                            <div className="text-sm text-gray-600">{role.description}</div>
                            <div className="text-xs text-gray-500 mt-1">
                              Level {role.level}
                              {role.is_system_role && ' • System Role'}
                            </div>
                          </div>
                          <button
                            onClick={() => handleRevokeRole(showRoleModal.id, role.id)}
                            className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            Revoke
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">No roles assigned yet.</p>
                  )}
                </div>

                {/* Assign New Role */}
                <div>
                  <h4 className="text-md font-semibold mb-3 text-gray-800">Assign New Role</h4>
                  <div className="flex gap-3">
                    <select
                      value={selectedRoleId}
                      onChange={(e) => setSelectedRoleId(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select a role...</option>
                      {roles
                        .filter(role => !userRoles.some(ur => ur.id === role.id))
                        .map((role) => (
                          <option key={role.id} value={role.id}>
                            {/* BUG-272/276: humanised role name in assign dropdown */}
                            {(role.display_name || formatRoleName(role.name))} (Level {role.level})
                            {role.is_system_role && ' - System Role'}
                          </option>
                        ))}
                    </select>
                    <button
                      onClick={() => handleAssignRole(showRoleModal.id)}
                      disabled={!selectedRoleId}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-300 transition-colors"
                    >
                      Assign
                    </button>
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t">
                  <button
                    onClick={() => {
                      setShowRoleModal(null);
                      setUserRoles([]);
                      setSelectedRoleId('');
                    }}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Manage Site Roles Modal */}
      {SITE_ROLES_ENABLED && showSiteRolesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  <MapPin className="w-5 h-5 inline-block mr-2 text-teal-600" />
                  Site Roles - {showSiteRolesModal.name}
                </h3>
                <p className="text-sm text-gray-600">{showSiteRolesModal.email}</p>
              </div>
              <button
                onClick={() => {
                  setShowSiteRolesModal(null);
                  setUserSiteRoles([]);
                  setSiteRoleError(null);
                  setRevokeConfirmId(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {siteRoleLoading && userSiteRoles.length === 0 ? (
              <div className="text-center py-8 text-gray-500">Loading site roles...</div>
            ) : (
              <div>
                {/* Current Site Roles */}
                <div className="mb-6">
                  <h4 className="text-md font-semibold mb-3 text-gray-800">Current Site Assignments</h4>
                  {userSiteRoles.length > 0 ? (
                    <div className="space-y-2">
                      {userSiteRoles.map((sr) => {
                        const isActive = !sr.revoked && (!sr.expires_at || new Date(sr.expires_at) > new Date());
                        return (
                          <div
                            key={sr.id}
                            className={`flex items-center justify-between p-3 border rounded-lg ${isActive ? 'bg-gray-50' : 'bg-red-50 opacity-70'}`}
                          >
                            <div>
                              <div className="font-medium text-gray-900">
                                {sr.role_name}
                                {sr.scope_type && (
                                  <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">
                                    {sr.scope_type}
                                  </span>
                                )}
                              </div>
                              <div className="text-sm text-gray-600">
                                {sr.site_name || (sr.site_node_id ? sr.site_node_id : 'Corporate (All Sites)')}
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                {sr.assigned_at && `Assigned ${new Date(sr.assigned_at).toLocaleDateString()}`}
                                {sr.expires_at && ` | Expires ${new Date(sr.expires_at).toLocaleDateString()}`}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {sr.revoked ? (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700">Revoked</span>
                              ) : !isActive ? (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700">Expired</span>
                              ) : revokeConfirmId === sr.id ? (
                                <div className="flex items-center gap-1">
                                  <span className="text-xs text-red-600">Revoke?</span>
                                  <button
                                    onClick={() => handleRevokeSiteRole(showSiteRolesModal.id, sr.id)}
                                    className="px-2 py-1 text-xs text-white bg-red-600 hover:bg-red-700 rounded transition-colors"
                                  >
                                    Yes
                                  </button>
                                  <button
                                    onClick={() => setRevokeConfirmId(null)}
                                    className="px-2 py-1 text-xs text-gray-600 border border-gray-300 hover:bg-gray-50 rounded transition-colors"
                                  >
                                    No
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => setRevokeConfirmId(sr.id)}
                                  className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                >
                                  Revoke
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">No site role assignments yet.</p>
                  )}
                </div>

                {/* Assign New Site Role */}
                <div className="border-t pt-4">
                  <h4 className="text-md font-semibold mb-3 text-gray-800">Assign Site Role</h4>
                  {siteRoleError && (
                    <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                      {siteRoleError}
                    </div>
                  )}
                  <div className="grid grid-cols-1 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Site</label>
                      <select
                        value={siteRoleForm.site_node_id}
                        onChange={(e) => setSiteRoleForm({ ...siteRoleForm, site_node_id: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      >
                        <option value="">Corporate (All Sites)</option>
                        {sites.map(s => (
                          <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-400 mt-1">Leave empty for corporate-level role</p>
                    </div>
                    <div className="flex gap-3">
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-gray-600 mb-1">Role</label>
                        <select
                          value={siteRoleForm.role_id}
                          onChange={(e) => setSiteRoleForm({ ...siteRoleForm, role_id: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        >
                          <option value="">Select role...</option>
                          {roles.map(r => (
                            <option key={r.id} value={r.id}>
                              {r.name} (Level {r.level})
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-gray-600 mb-1">Expires At</label>
                        <input
                          type="date"
                          value={siteRoleForm.expires_at}
                          onChange={(e) => setSiteRoleForm({ ...siteRoleForm, expires_at: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        />
                      </div>
                    </div>
                    <button
                      onClick={() => handleAssignSiteRole(showSiteRolesModal.id)}
                      disabled={!siteRoleForm.role_id || siteRoleLoading}
                      className="flex items-center justify-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:bg-teal-300 transition-colors text-sm"
                    >
                      <Plus className="w-4 h-4" />
                      {siteRoleLoading ? 'Assigning...' : 'Assign Site Role'}
                    </button>
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t">
                  <button
                    onClick={() => {
                      setShowSiteRolesModal(null);
                      setUserSiteRoles([]);
                      setSiteRoleError(null);
                      setRevokeConfirmId(null);
                    }}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* View Permissions Modal */}
      {showPermissionsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[80vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900">{showPermissionsModal.name} - Roles & Permissions</h3>
                <p className="text-sm text-gray-600">{showPermissionsModal.email}</p>
              </div>
              <button
                onClick={() => {
                  setShowPermissionsModal(null);
                  setUserPermissions(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {roleLoading ? (
              <div className="text-center py-8 text-gray-500">Loading permissions...</div>
            ) : userPermissions ? (
              <div>
                {/* Assigned Roles */}
                <div className="mb-6">
                  <h4 className="text-md font-semibold mb-3 text-gray-800">Assigned Roles</h4>
                  <div className="flex flex-wrap gap-2">
                    {userPermissions.roles && userPermissions.roles.length > 0 ? (
                      userPermissions.roles.map((role) => (
                        <span
                          key={role.id}
                          className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                        >
                          {/* BUG-272 */}
                          {(role.display_name || formatRoleName(role.name))} (Level {role.level})
                        </span>
                      ))
                    ) : (
                      <span className="text-gray-500 text-sm">No roles assigned</span>
                    )}
                  </div>
                </div>

                {/* Permissions */}
                <div>
                  <h4 className="text-md font-semibold mb-3 text-gray-800">
                    Effective Permissions ({userPermissions.permissions?.length || 0})
                  </h4>
                  {userPermissions.permissions && userPermissions.permissions.length > 0 ? (
                    <div className="space-y-4">
                      {Object.entries(
                        userPermissions.permissions.reduce((acc, perm) => {
                          if (!acc[perm.resource]) acc[perm.resource] = [];
                          acc[perm.resource].push(perm);
                          return acc;
                        }, {})
                      ).map(([resource, perms]) => (
                        <div key={resource} className="border rounded-lg p-4 bg-gray-50">
                          <h5 className="font-semibold text-gray-900 mb-2 capitalize">{resource}</h5>
                          <div className="flex flex-wrap gap-2">
                            {perms.map((perm) => (
                              <span
                                key={perm.id}
                                className="px-2 py-1 bg-white border border-gray-200 text-gray-700 rounded text-sm"
                              >
                                {perm.action}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">No specific permissions assigned.</p>
                  )}
                </div>

                <div className="mt-6 pt-4 border-t">
                  <button
                    onClick={() => {
                      setShowPermissionsModal(null);
                      setUserPermissions(null);
                    }}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No permissions found for this user
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagementTable;
